import { BookOpen } from 'lucide-react';

import { ErrorState } from '@/components/ui/ErrorState';
import { Spinner } from '@/components/ui/Spinner';
import { useGlossary } from '@/features/catalog/hooks';
import type { ApiError } from '@/lib/errors';

export function GlossaryPage(): JSX.Element {
  const glossary = useGlossary();

  if (glossary.isPending) {
    return <Spinner label="Loading glossary…" />;
  }
  if (glossary.isError) {
    return <ErrorState error={glossary.error as ApiError} />;
  }

  if (glossary.data.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-ink-300 dark:border-white/15 bg-white/60 dark:bg-white/5 p-10 text-center">
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 dark:bg-brand-900/30 text-brand-600">
          <BookOpen className="h-4 w-4" />
        </div>
        <p className="mt-3 text-sm font-medium text-ink-700 dark:text-ink-200">No glossary terms defined yet.</p>
        <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
          Business terms appear here once stewards submit them via the Catalogue tab.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="h-section">Governed terms</h3>
        <span className="font-mono text-[10px] tracking-[0.18em] text-ink-500 dark:text-ink-400">
          {glossary.data.length} TERM(S)
        </span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {glossary.data.map((term) => (
          <article
            key={term.id}
            className="group relative overflow-hidden rounded-xl border border-ink-200/70 dark:border-white/10 bg-white dark:bg-ink-900 p-5 shadow-soft transition hover:-translate-y-0.5 hover:border-ink-300 hover:shadow-elevated"
          >
            <div className="pointer-events-none absolute -top-12 -right-12 h-32 w-32 rounded-full bg-brand-100/50 opacity-0 blur-2xl transition group-hover:opacity-100" />
            <div className="relative flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-brand-400 to-brand-700 text-white shadow-glow">
                <BookOpen className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <h4 className="truncate font-display text-sm font-semibold tracking-tight text-ink-900 dark:text-ink-50">
                  {term.name}
                </h4>
                <p className="mt-0.5 truncate font-mono text-[11px] text-ink-500 dark:text-ink-400">{term.fqn}</p>
              </div>
            </div>
            {term.description && (
              <p className="relative mt-3 text-sm leading-relaxed text-ink-600 dark:text-ink-300">
                {term.description}
              </p>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}
